import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Award, Sparkles, TrendingDown, TrendingUp, Activity, Brain, Heart, Calendar } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { STEPS, BODY_REGIONS } from "../data/courseData";
import logoMark from "../assets/logo-mark.png";

const ALL_REGIONS = [...BODY_REGIONS.front, ...BODY_REGIONS.back];
const labelFor = (id) => ALL_REGIONS.find((r) => r.id === id)?.label || id;

// Overall transformation rating tiers
function computeRating({ painDelta, surveyDelta, exercises, completedSteps }) {
  // higher score = bigger transformation
  let score = 0;
  if (painDelta != null && painDelta < 0) score += Math.min(40, Math.abs(painDelta) * 8); // up to 40
  if (surveyDelta != null && surveyDelta < 0) score += Math.min(30, Math.abs(surveyDelta) * 0.6); // up to 30
  score += Math.min(20, (exercises / 20) * 20); // up to 20 for completion
  score += completedSteps * 2.5; // up to 10

  if (score >= 75) return { tier: "Profound Transformation", color: "#10B981", message: "You have done something most people never even attempt. The version of you that walked in here has been fundamentally rewritten." };
  if (score >= 55) return { tier: "Major Transformation", color: "#22D3EE", message: "Your body, mind, and nervous system are operating in measurably different ways. The Reset State is now familiar territory." };
  if (score >= 35) return { tier: "Significant Shift", color: "#38BDF8", message: "You've broken patterns that took years to build. This is real, durable change — keep reinforcing it daily." };
  if (score >= 20) return { tier: "Strong Foundation", color: "#F59E0B", message: "You've laid the foundation. The work now is repetition. Every day you choose the new pattern, it gets more 'you'." };
  return { tier: "Awareness Activated", color: "#F59E0B", message: "You have done the most important thing — you started seeing the system. From here, every step is rewiring." };
}

export default function Graduation() {
  const { user } = useAuth();
  const [scans, setScans] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [journals, setJournals] = useState([]);
  const [progress, setProgress] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/pain-scans").then((r) => setScans(r.data));
    api.get("/surveys").then((r) => setSurveys(r.data));
    api.get("/journal").then((r) => setJournals(r.data));
    api.get("/step-progress").then((r) => setProgress(r.data));
    api.get("/daily-checkin").then((r) => setCheckins(r.data));
  }, []);

  const stats = useMemo(() => {
    const sortedScans = [...scans].sort((a, b) => a.created_at.localeCompare(b.created_at));
    const sortedSurveys = [...surveys].sort((a, b) => a.created_at.localeCompare(b.created_at));
    const firstScan = sortedScans[0];
    const lastScan = sortedScans[sortedScans.length - 1];
    const firstSurvey = sortedSurveys[0];
    const lastSurvey = sortedSurveys[sortedSurveys.length - 1];

    const painDelta = firstScan && lastScan ? +(lastScan.average_intensity - firstScan.average_intensity).toFixed(1) : null;
    const painPct = firstScan && lastScan && firstScan.average_intensity > 0
      ? Math.round(((firstScan.average_intensity - lastScan.average_intensity) / firstScan.average_intensity) * 100)
      : null;

    const surveyDelta = firstSurvey && lastSurvey && firstSurvey.score != null && lastSurvey.score != null
      ? +(lastSurvey.score - firstSurvey.score).toFixed(0)
      : null;

    const exercises = progress.reduce((a, p) => a + (p.exercises_completed?.length || 0), 0);
    const completedSteps = progress.filter((p) => p.is_complete).length;

    // Top improved body regions (first scan vs last scan)
    const firstMap = new Map((firstScan?.regions || []).map((r) => [r.region, r.intensity]));
    const lastMap = new Map((lastScan?.regions || []).map((r) => [r.region, r.intensity]));
    const allKeys = new Set([...firstMap.keys(), ...lastMap.keys()]);
    const regionDeltas = [...allKeys].map((k) => ({
      region: k,
      from: firstMap.get(k) ?? 0,
      to: lastMap.get(k) ?? 0,
      delta: (lastMap.get(k) ?? 0) - (firstMap.get(k) ?? 0),
    })).sort((a, b) => a.delta - b.delta);
    const topImproved = regionDeltas.filter((r) => r.delta < 0).slice(0, 3);

    // Days on journey
    const createdAt = user?.created_at ? new Date(user.created_at) : null;
    const days = createdAt ? Math.max(1, Math.round((Date.now() - createdAt.getTime()) / 86400000)) : null;

    // Check-in averages
    const lastWeek = checkins.slice(-7);
    const lastWeekAvgPain = lastWeek.length ? +(lastWeek.reduce((a, c) => a + c.pain_score, 0) / lastWeek.length).toFixed(1) : null;
    const lastWeekAvgAlign = lastWeek.length ? +(lastWeek.reduce((a, c) => a + c.alignment_score, 0) / lastWeek.length).toFixed(1) : null;

    return {
      firstScan, lastScan, painDelta, painPct,
      surveyDelta, firstSurvey, lastSurvey,
      exercises, completedSteps,
      topImproved, days,
      journals: journals.length,
      lastWeekAvgPain, lastWeekAvgAlign,
    };
  }, [scans, surveys, progress, journals, checkins, user]);

  const rating = computeRating(stats);

  return (
    <div className="relative min-h-screen text-white px-6 py-10 pb-16">
      <div className="bps-ambient" />
      <div className="max-w-md mx-auto">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          {/* Hero */}
          <div className="text-center mb-10">
            <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-4 bps-pulse-glow bg-[#03101F]/70 border border-[#22D3EE]/30 overflow-hidden">
              <img
                src={logoMark}
                alt="Back Pain Solution"
                data-testid="grad-logo-mark"
                className="w-full h-full object-cover scale-110 drop-shadow-[0_0_18px_rgba(34,211,238,0.55)] select-none"
                draggable={false}
              />
            </div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-white/60 font-mono">Graduation</div>
            <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tighter mt-3">Reset Mind.<br/>Reprogrammed Body.</h1>
            <p className="text-white/60 text-sm mt-4 max-w-xs mx-auto">You are no longer the person who arrived here.</p>
          </div>

          {/* FINAL REPORT — overall rating */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="rounded-3xl border border-white/10 backdrop-blur-xl p-6 relative overflow-hidden"
            style={{ background: `linear-gradient(160deg, ${rating.color}22, rgba(10,21,48,0.6))` }}
            data-testid="final-report"
          >
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-50" style={{ background: `radial-gradient(circle, ${rating.color}55, transparent 70%)` }} />
            <div className="bps-eyebrow text-white/60">Final Report</div>
            <h2 className="font-display text-3xl font-light tracking-tighter mt-2" style={{ color: rating.color }} data-testid="rating-tier">{rating.tier}</h2>
            <p className="text-sm text-white/85 mt-3 leading-relaxed">{rating.message}</p>

            {/* Headline metric tiles */}
            <div className="mt-5 grid grid-cols-2 gap-3">
              <ReportTile
                icon={Activity}
                label="Pain shift"
                value={stats.painPct != null ? `${stats.painPct > 0 ? "-" : "+"}${Math.abs(stats.painPct)}%` : "—"}
                sub={stats.painDelta != null ? `${stats.painDelta > 0 ? "+" : ""}${stats.painDelta} points` : "no data"}
                color={stats.painDelta != null && stats.painDelta < 0 ? "#10B981" : "#7C8FA8"}
                testid="report-pain"
              />
              <ReportTile
                icon={Brain}
                label="Mental load"
                value={stats.surveyDelta != null ? `${stats.surveyDelta > 0 ? "+" : ""}${stats.surveyDelta}` : "—"}
                sub={stats.surveyDelta != null && stats.surveyDelta < 0 ? "lighter than day one" : stats.surveyDelta != null ? "still working through" : "no data"}
                color={stats.surveyDelta != null && stats.surveyDelta < 0 ? "#22D3EE" : "#7C8FA8"}
                testid="report-mental"
              />
              <ReportTile
                icon={Heart}
                label="Practices"
                value={`${stats.exercises}/16`}
                sub={`${stats.completedSteps} of 4 weeks`}
                color="#22D3EE"
                testid="report-exercises"
              />
              <ReportTile
                icon={Calendar}
                label="On the journey"
                value={stats.days != null ? `${stats.days}d` : "—"}
                sub={`${stats.journals} journal entries`}
                color="#F59E0B"
                testid="report-days"
              />
            </div>
          </motion.div>

          {/* Body — biggest wins */}
          {stats.topImproved.length > 0 && (
            <div className="mt-6 rounded-3xl bg-white/[0.04] border border-white/10 p-6" data-testid="biggest-wins">
              <div className="bps-eyebrow mb-3 flex items-center gap-2"><TrendingDown size={12} className="text-[#10B981]" /> Biggest body wins</div>
              <ul className="space-y-3">
                {stats.topImproved.map((r) => (
                  <li key={r.region} className="flex items-center gap-3 text-sm" data-testid={`win-${r.region}`}>
                    <span className="w-2 h-8 rounded-full bg-[#10B981]" style={{ boxShadow: "0 0 10px rgba(16,185,129,0.55)" }} />
                    <div className="flex-1">
                      <div className="text-white/90">{labelFor(r.region)}</div>
                      <div className="text-[11px] text-white/50 font-mono">{r.from} → {r.to}</div>
                    </div>
                    <div className="font-display text-lg text-[#10B981]">{r.delta}</div>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-white/55 italic mt-4">These areas held the most tension when you began. Your nervous system learned to release them.</p>
            </div>
          )}

          {/* Before vs After scan + survey */}
          <div className="mt-6 rounded-3xl bg-white/[0.04] border border-white/10 p-6" data-testid="before-after-card">
            <div className="bps-eyebrow text-white/60 mb-4">Before vs After</div>
            <div className="grid grid-cols-2 gap-4">
              <Metric label="Average pain" before={stats.firstScan?.average_intensity} after={stats.lastScan?.average_intensity} suffix="/10" lowerIsBetter />
              <Metric label="Mental load" before={stats.firstSurvey?.score} after={stats.lastSurvey?.score} suffix="/100" lowerIsBetter />
            </div>
            {stats.lastWeekAvgPain != null && (
              <div className="mt-5 pt-4 border-t border-white/5 grid grid-cols-2 gap-4">
                <MiniStat label="Last 7d avg pain" value={`${stats.lastWeekAvgPain}/10`} color="#F87171" />
                <MiniStat label="Last 7d avg alignment" value={`${stats.lastWeekAvgAlign}/10`} color="#10B981" />
              </div>
            )}
          </div>

          {/* Steps recap */}
          <div className="mt-6 rounded-3xl bg-white/[0.04] border border-white/10 p-6" data-testid="steps-recap">
            <div className="bps-eyebrow text-white/60 mb-3">The 4 weeks you moved through</div>
            <div className="grid grid-cols-2 gap-3">
              {STEPS.map((s) => {
                const p = progress.find((x) => x.step === s.step);
                const done = p?.is_complete;
                return (
                  <div key={s.step} className="rounded-2xl p-3 bg-white/5 border border-white/5">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-mono" style={{ color: s.accent }}>Week {s.step}</div>
                    <div className="text-sm mt-1 text-white">{s.title}</div>
                    <div className="text-[10px] text-white/50 mt-1">{p?.exercises_completed?.length || 0}/4 exercises{done ? " · complete" : ""}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Certificate */}
          <div className="mt-6 rounded-3xl border border-[#22D3EE]/25 p-6 relative overflow-hidden" style={{ background: "linear-gradient(160deg, rgba(34,211,238,0.10), rgba(10,21,48,0.6))" }} data-testid="certificate">
            <div className="absolute inset-0 opacity-30" style={{
              backgroundImage: "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
              backgroundSize: "20px 20px",
            }} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-1">
                <img
                  src={logoMark}
                  alt=""
                  className="w-9 h-9 rounded-xl object-cover drop-shadow-[0_0_14px_rgba(34,211,238,0.45)] select-none"
                  draggable={false}
                />
                <div className="bps-eyebrow text-white/60">Certificate</div>
              </div>
              <h3 className="font-display text-3xl mt-2 tracking-tighter font-light">I Am Transformed.</h3>
              <p className="text-white/75 text-sm mt-3 leading-relaxed">
                Awarded to <span className="text-white font-medium">{user?.name}</span> for completing the Back Pain Solution
                <span className="text-white"> 4-Week Mind–Body Pain Reprogramming System</span>. The Back Pain Transformer
                continues to support the alignment you have rebuilt.
              </p>
              <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-2 text-xs text-white/60">
                <Sparkles size={12} className="text-[#22D3EE]" />
                {stats.days != null ? `${stats.days} days from start to graduation` : "Journey complete"}
              </div>
            </div>
          </div>

          <button onClick={() => navigate("/home")} data-testid="grad-back-home" className="mt-8 w-full py-4 rounded-full bg-[#22D3EE] text-[#03101F] font-semibold shadow-[0_0_24px_rgba(34,211,238,0.4)] hover:-translate-y-0.5 transition-all">
            Return Home
          </button>
        </motion.div>
      </div>
    </div>
  );
}

function ReportTile({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/5 p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-white/55"><Icon size={11} />{label}</div>
      <div className="font-display text-2xl font-medium mt-1" style={{ color }}>{value}</div>
      <div className="text-[11px] text-white/55 mt-0.5">{sub}</div>
    </div>
  );
}

function Metric({ label, before, after, suffix, lowerIsBetter = true }) {
  const hasData = before != null && after != null;
  const improved = hasData && (lowerIsBetter ? after < before : after > before);
  const Icon = improved ? TrendingDown : TrendingUp;
  return (
    <div className="rounded-2xl bg-white/5 p-4 border border-white/5">
      <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-white/55">{label}</div>
      <div className="mt-2 flex items-end gap-2">
        <div className="text-white/45 text-sm">{before ?? "—"}{before != null ? suffix : ""}</div>
        <div className="text-white/45">→</div>
        <div className="font-display text-2xl">{after ?? "—"}{after != null ? suffix : ""}</div>
      </div>
      {hasData && (
        <div className={`mt-1 inline-flex items-center gap-1 text-[11px] font-mono ${improved ? "text-[#10B981]" : "text-[#F87171]"}`}>
          <Icon size={11} />{improved ? "improved" : "rising"}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-white/55">{label}</div>
      <div className="font-display text-lg mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
